// ── Integrations → Notifications ────────────────────────────────────────────
//
// The admin screen for outbound notification email.
//
// A REAL MODULE, not another function nested inside App.js. IntegrationWFPage
// and IntegrationPlaidPage live inline there, closing over the enclosing scope,
// which is why App.js is 150KB and why there is a stale App-1.js carrying a
// second copy of both. MailCaptureAdminPage is the pattern worth following.
//
// The Integrations section is not data-driven: there is no list of providers
// anywhere, just one hand-written page per provider plus a nav entry and a
// mount line in App.js. So a new row in dbo.integrations renders nothing until
// a page like this exists — which is exactly what happened here.
//
// ── THREE SWITCHES, AND THEY ARE NOT THE SAME (Sep 2026) ───────────────────
//
//   Active (below)     pauses DELIVERY. Everything still queues and goes out
//                      when it is switched back on. A pause, not a loss.
//   Per event          stops that event QUEUING at all. Switching it back on
//                      starts it sending from that moment; there is no backlog
//                      to release, because nothing was written.
//   The permission flag  decides WHO. That lives in User Management and
//                      deliberately has no copy here.
//
// The preview shown beside each event is rendered by the SERVER, from the same
// template code that sends the real thing. It would have been easy to rebuild
// token substitution in this file for a live preview while typing, and it would
// have been the usual mistake: two implementations of one rule, agreeing right
// up until somebody fixes an escaping bug in only one of them. So the preview
// refreshes on save rather than on keystroke.

import { org } from "../../../shared/org";
import { useState, useEffect, useCallback } from "react";
import { B } from "../../../shared/theme";
import { Btn, Card, ErrorMsg, SectionHeader, Spinner, Td, Th } from "../../../shared/ui";
import { useApp } from "../../../shared/appContext";

const PROVIDER = "m365send";
const SECRET   = "m365send-clientsecret";

const row = { display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 12 };
const lbl = { width: 190, fontSize: 13, fontWeight: 500, paddingTop: 7, flex: "0 0 auto" };
const inp = { flex: 1, padding: "7px 10px", fontSize: 13, borderRadius: 6,
              border: "1px solid var(--color-border-secondary, #ccd3d8)",
              background: "var(--color-background-primary, #fff)",
              color: "var(--color-text-primary, #1a1a1a)", boxSizing: "border-box" };
const hint = { fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: 4, lineHeight: 1.5 };
const area = { ...inp, width: "100%", minHeight: 70, resize: "vertical",
               fontFamily: "inherit", lineHeight: 1.5 };

const STATUS_TONE = {
  sent:    { bg: "#dff3ec", fg: "#0F6E56" },
  pending: { bg: "#fdeccd", fg: "#7a4f00" },
  failed:  { bg: "#f7d9cf", fg: "#993C1D" },
};
const pill = (s) => {
  const t = STATUS_TONE[s] || { bg: "var(--color-background-secondary)", fg: "var(--color-text-secondary)" };
  return { display: "inline-block", padding: "1px 7px", borderRadius: 4, fontSize: 11,
           fontWeight: 600, background: t.bg, color: t.fg, whiteSpace: "nowrap" };
};

const SLOTS = [
  ["subject", "Subject",  false, "The inbox row. Plain text — no formatting reaches it."],
  ["heading", "Heading",  false, "The large line at the top of the email, and the title on the in-app bell."],
  ["intro",   "Opening",  true,  "The paragraph before the figures. A blank line starts a new paragraph."],
  ["closing", "Closing",  true,  "The paragraph after the figures — usually the instruction. Clear it to remove the paragraph entirely."],
];

// ── One event ──────────────────────────────────────────────────────────────
const EventEditor = ({ ev, busy, onSave }) => {
  const [open, setOpen]     = useState(false);
  const [draft, setDraft]   = useState({});
  const [showRaw, setShowRaw] = useState(false);

  // The editor shows the override when there is one and the shipped default as
  // a PLACEHOLDER when there is not, so an empty box always means "using the
  // default" and never "this email has no subject".
  const valueOf = (k) => (k in draft ? draft[k] : (ev.overrides[k] == null ? "" : ev.overrides[k]));
  const dirty   = Object.keys(draft).length > 0;
  const overridden = (k) => (k in draft ? draft[k] !== null : ev.overrides[k] != null);

  const save = () => {
    // Only the slots actually touched are sent. An untouched slot is omitted
    // entirely, which the endpoint reads as "leave it alone" — distinct from
    // sending null, which clears the override.
    const payload = { eventkey: ev.eventkey };
    for (const k of Object.keys(draft)) payload[k] = draft[k];
    onSave(payload, () => setDraft({}));
  };

  const restoreAll = () =>
    onSave({ eventkey: ev.eventkey, subject: null, heading: null, intro: null, closing: null },
           () => setDraft({}));

  const anyOverride = SLOTS.some(([k]) => ev.overrides[k] != null);

  return (
    <div style={{ borderTop: "1px solid var(--color-border-tertiary, #e6e9eb)", padding: "12px 0" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 7, paddingTop: 2, cursor: "pointer" }}
               title={ev.isactive ? "Switch this event off" : "Switch this event on"}>
          <input type="checkbox" checked={ev.isactive} disabled={busy || !ev.registered}
                 onChange={e => onSave({ eventkey: ev.eventkey, isactive: e.target.checked })} />
        </label>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 14, fontWeight: 600,
                           opacity: ev.isactive ? 1 : 0.55 }}>{ev.displayname}</span>
            <code style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{ev.eventkey}</code>
            {!ev.isactive && <span style={pill("failed")}>off</span>}
            {anyOverride && <span style={pill("pending")}>edited</span>}
            {!ev.registered && <span style={pill("pending")}>not in database</span>}
          </div>

          {ev.description && (
            <div style={{ ...hint, marginTop: 5, maxWidth: 780 }}>{ev.description}</div>
          )}
          {ev.recipientnote && (
            <div style={{ ...hint, marginTop: 3 }}>
              Goes to: <strong>{ev.recipientnote}</strong>. Change that in User Management —
              there is no recipient list here on purpose.
            </div>
          )}
          {ev.updatedby && (
            <div style={{ ...hint, marginTop: 3 }}>
              Wording last edited by {ev.updatedby}
              {ev.updatedat ? ` on ${String(ev.updatedat).replace("T", " ").slice(0, 16)}` : ""}.
            </div>
          )}
        </div>

        <Btn onClick={() => setOpen(o => !o)} disabled={!ev.registered}>
          {open ? "Close" : "Edit wording"}
        </Btn>
      </div>

      {open && (
        <div style={{ marginTop: 14, paddingLeft: 26 }}>
          {ev.tokens && ev.tokens.length > 0 && (
            <div style={{ marginBottom: 12, padding: "9px 11px", borderRadius: 7,
                          background: "var(--color-background-secondary, #f5f7f8)" }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, marginBottom: 6 }}>
                Placeholders for this event
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {ev.tokens.map(([t, desc]) => (
                  <span key={t} title={desc}
                        style={{ fontFamily: "var(--font-mono)", fontSize: 11, padding: "2px 6px",
                                 borderRadius: 4, background: "var(--color-background-primary, #fff)",
                                 border: "1px solid var(--color-border-tertiary, #e6e9eb)" }}>
                    {`{${t}}`}
                  </span>
                ))}
              </div>
              <div style={{ ...hint, marginTop: 7 }}>
                Anything else you type is plain text — HTML tags will show as tags rather than
                formatting. A placeholder that is misspelt stays visible in the preview instead of
                disappearing, so a typo is something you can see.
              </div>
            </div>
          )}

          {SLOTS.map(([k, label, multi, help]) => (
            <div key={k} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                <label style={{ fontSize: 13, fontWeight: 500 }}>{label}</label>
                {overridden(k)
                  ? <button type="button" onClick={() => setDraft(d => ({ ...d, [k]: null }))}
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                                     fontSize: 11.5, color: B, textDecoration: "underline" }}>
                      use default
                    </button>
                  : <span style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>using default</span>}
              </div>
              {multi
                ? <textarea value={valueOf(k) || ""} style={area} disabled={busy}
                            placeholder={ev.defaults[k] || "(no default — this slot is empty)"}
                            onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} />
                : <input value={valueOf(k) || ""} style={{ ...inp, width: "100%" }} disabled={busy}
                         placeholder={ev.defaults[k] || ""}
                         onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} />}
              <div style={hint}>{help}</div>
            </div>
          ))}

          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
            <Btn variant="primary" onClick={save} disabled={busy || !dirty}>
              {busy ? "Saving…" : "Save wording"}
            </Btn>
            {anyOverride && <Btn onClick={restoreAll} disabled={busy}>Restore all defaults</Btn>}
            {dirty && <span style={{ fontSize: 12, color: "#7a4f00" }}>Unsaved changes.</span>}
          </div>

          {/* ── Preview ──────────────────────────────────────────────────── */}
          <div style={{ borderTop: "1px dashed var(--color-border-tertiary, #e6e9eb)", paddingTop: 12 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>Preview</span>
              <span style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>
                sample data, as currently saved — save to refresh
              </span>
              <button type="button" onClick={() => setShowRaw(r => !r)}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                               fontSize: 11.5, color: B, textDecoration: "underline" }}>
                {showRaw ? "hide bell text" : "show bell text"}
              </button>
            </div>

            {ev.preview && ev.preview.error ? (
              <div style={{ padding: "9px 12px", borderRadius: 7, fontSize: 12.5,
                            background: "#f7d9cf", color: "#993C1D" }}>
                This event will not render: {ev.preview.error}
              </div>
            ) : ev.preview ? (
              <>
                <div style={{ fontSize: 12.5, marginBottom: 8 }}>
                  <span style={{ color: "var(--color-text-secondary)" }}>Subject: </span>
                  <strong>{ev.preview.subject}</strong>
                </div>
                {showRaw && (
                  <div style={{ fontSize: 12.5, marginBottom: 8, padding: "8px 10px", borderRadius: 6,
                                background: "var(--color-background-secondary, #f5f7f8)" }}>
                    <div style={{ color: "var(--color-text-secondary)", marginBottom: 3 }}>
                      In-app bell
                    </div>
                    <div><strong>{ev.preview.title}</strong></div>
                    <div>{ev.preview.body}</div>
                  </div>
                )}
                {/* An iframe because this is EMAIL html — tables and inline styles
                    meant for Outlook. Dropped into the page directly it would
                    inherit the app's CSS and stop looking like what arrives. */}
                <iframe title={`preview-${ev.eventkey}`} srcDoc={ev.preview.html} sandbox=""
                        style={{ width: "100%", height: 380, border: "1px solid var(--color-border-tertiary, #e6e9eb)",
                                 borderRadius: 7, background: "#f4f6f7" }} />
              </>
            ) : (
              <div style={{ fontSize: 12.5, color: "var(--color-text-secondary)" }}>No preview available.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const IntegrationNotificationsPage = () => {
  const { api } = useApp();

  const [loading, setLoading]   = useState(true);
  const [err, setErr]           = useState(null);
  const [msg, setMsg]           = useState(null);
  const [busy, setBusy]         = useState(false);

  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [sender, setSender]     = useState("");
  const [domains, setDomains]   = useState("");
  const [active, setActive]     = useState(false);
  const [secret, setSecret]     = useState("");
  const [status, setStatus]     = useState({});
  const [stats, setStats]       = useState(null);
  const [evts, setEvts]         = useState(null);
  // One monitoring sweep queues a row per finding per portfolio manager, so
  // the queue is no longer something you read top to bottom looking for the
  // one you want. Filtering client-side: the rows are already here.
  const [qFilter, setQFilter]   = useState("");

  const load = useCallback(async () => {
    setErr(null);
    try {
      const data = await api.get("getIntegrations", { provider: PROVIDER });
      const c = (data && data.config) || {};
      setTenantId(c.tenantid || "");
      setClientId(c.clientid || "");
      setSender(c.sender || "");
      setDomains((c.allowdomains || []).join(", "));
      setActive(!!(data && data.isactive));
      setStatus((data && data.secretstatus) || {});
      // The queue is a separate read and is allowed to fail on its own: before
      // the migration runs there is no outbox table, and that must not stop the
      // configuration form from rendering — configuring it is how you get there.
      try { setStats(await api.post("runNotifications", { stats: true })); }
      catch { setStats(null); }
      // Same reasoning for the event list and its own, later migration.
      try { setEvts(await api.get("getNotificationEvents")); }
      catch { setEvts(null); }
    } catch (e) { setErr(e.message); }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setMsg(null);
    try {
      const secrets = {};
      // Omitted when blank — that is what makes "leave blank to keep" work,
      // rather than overwriting a good secret with an empty string.
      if (secret && secret.trim() !== "") secrets[SECRET] = secret.trim();

      const config = {
        tenantid: tenantId.trim(),
        clientid: clientId.trim(),
        secretname: SECRET,
        sender: sender.trim(),
        allowdomains: domains.split(",").map(s => s.trim().replace(/^@/, "")).filter(Boolean),
      };
      const res = await api.post("saveIntegration", {
        provider: PROVIDER,
        displayname: "Microsoft 365 — outbound notifications",
        config, secrets, isactive: active,
      });
      setStatus(res.secretstatus || status);
      setSecret("");
      setMsg({ type: "ok", text: "Saved." });
      load();
    } catch (e) { setMsg({ type: "err", text: e.message }); }
    setBusy(false);
  };

  const saveEvent = async (payload, done) => {
    setBusy(true); setMsg(null);
    try {
      await api.post("saveNotificationEvent", payload);
      if (done) done();
      setMsg({ type: "ok", text: "Saved." });
      // Reloaded rather than patched in place, because the preview has to be
      // re-rendered by the server that owns the templates.
      try { setEvts(await api.get("getNotificationEvents")); } catch { /* keep what we had */ }
    } catch (e) { setMsg({ type: "err", text: e.message }); }
    setBusy(false);
  };

  const act = async (body, okText) => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("runNotifications", body);
      setMsg({ type: "ok", text: typeof okText === "function" ? okText(r) : okText });
      load();
    } catch (e) { setMsg({ type: "err", text: e.message }); }
    setBusy(false);
  };

  // ── RAISING THE FINDINGS, AS OPPOSED TO SENDING WHAT WAS RAISED ──────────
  //
  // Two different jobs, and until now only the second one had a button.
  // runNotifications DRAINS the outbox; runMonitoringAlerts FILLS it by sweeping
  // dbo.vUCCWorklist and dbo.vTaxWorklist. Pressing "Send queued now" on an
  // empty outbox did nothing and said so, which reads as "there is nothing to
  // report" when it actually meant "nobody has looked yet".
  //
  // On the schedule the Logic App calls both in order. This is the manual half,
  // and it exists because nothing here should be discoverable only to whoever
  // remembers the endpoint name.
  //
  // Nothing is ordered or filed by either button. The sweep reads two views and
  // writes outbox rows; that is the whole of it.
  const sweep = async (dryRun) => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.post("runMonitoringAlerts", dryRun ? { dryRun: true } : {});
      if (dryRun) {
        const rows = (r.detail || []).filter(d => d.wouldQueue);
        setMsg({ type: "ok", text: rows.length
          ? `${rows.length} finding(s) would be raised — ` +
            rows.slice(0, 4).map(d => (d.company || "(no borrower)")).join(", ") +
            (rows.length > 4 ? ` and ${rows.length - 4} more` : "") +
            ". Nothing was queued."
          : "Nothing to raise. Both registers are clear." });
      } else {
        setMsg({ type: "ok", text:
          `${r.queued} queued, ${r.duplicate} already raised, ${r.skipped} skipped` +
          (r.failed ? `, ${r.failed} failed` : "") +
          (r.queued ? ". Press Send queued now to deliver them." : ".") });
      }
      load();
    } catch (e) { setMsg({ type: "err", text: e.message }); }
    setBusy(false);
  };

  if (loading) return <Spinner />;
  if (err) return <ErrorMsg msg={err} onRetry={load} />;

  const secretSet = !!status[SECRET];
  const counts = (stats && stats.byStatus) || [];
  // Matched across every column the table shows, so "NuWave" finds the row by
  // its record key and "monitoring" finds a whole sweep.
  const shownRecent = ((stats && stats.recent) || []).filter(r => {
    const q = qFilter.trim().toLowerCase();
    if (!q) return true;
    return [r.eventkey, r.entityid, r.recipient, r.status, r.lasterror, r.createdat]
      .some(v => String(v == null ? "" : v).toLowerCase().includes(q));
  });
  const countOf = s => (counts.find(x => x.status === s) || {}).n || 0;
  const failedCount = countOf("failed");
  const offCount = evts && evts.events ? evts.events.filter(e => !e.isactive).length : 0;

  return <>
    <SectionHeader title="Notification Email" actions={[
      <Btn key="t" onClick={() => act({ test: true }, "Test message sent — check the mailbox.")}
           disabled={busy}>Send test</Btn>,
      // Ordered the way the work runs: see what would be raised, raise it,
      // then deliver it. (A plain comment, not {/* */} — this is a JS array
      // literal, not JSX children.)
      <Btn key="dry" onClick={() => sweep(true)} disabled={busy}
           title="Sweeps the UCC and tax registers and reports what it would raise. Queues nothing, sends nothing.">
        Preview alerts
      </Btn>,
      <Btn key="sweep" onClick={() => sweep(false)} disabled={busy}
           title="Raises a notice for every current finding on the UCC and tax worklists. Writes outbox rows; sends nothing until you drain the queue.">
        Raise alerts now
      </Btn>,
      <Btn key="r" onClick={() => act({}, r => `${r.sent} sent, ${r.retrying} will retry, ${r.gaveUp} gave up.`)}
           disabled={busy}>Send queued now</Btn>,
    ]} />

    {msg && <div style={{ margin: "0 0 14px", padding: "9px 12px", borderRadius: 7, fontSize: 13,
                          background: msg.type === "ok" ? "#dff3ec" : "#f7d9cf",
                          color: msg.type === "ok" ? "#0F6E56" : "#993C1D" }}>{msg.text}</div>}

    {!active && (
      <div style={{ margin: "0 0 14px", padding: "10px 13px", borderRadius: 7, fontSize: 13,
                    background: "#fdeccd", color: "#7a4f00", lineHeight: 1.55 }}>
        <strong>Sending is switched off.</strong> Draws and ACH collections still queue their
        notifications; nothing leaves the building until Active is ticked. Turning it back on
        drains whatever built up, so this is a pause rather than a loss.
      </div>
    )}

    <Card style={{ padding: 18, marginBottom: 16 }}>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 16, lineHeight: 1.6 }}>
        Emails the people who hold the <strong>Draw Funding</strong> flag when a draw or an ACH
        collection is created. Change who is told by changing that flag in User Management —
        there is no separate list here on purpose.
        <br />
        Setup is in <code>doc/m365-notifications-setup.md</code>: this app registration holds no
        Graph permission, and Exchange grants it Mail.Send scoped to the one mailbox below.
      </div>

      <div style={row}>
        <label style={lbl}>Directory (tenant) ID</label>
        <div style={{ flex: 1 }}>
          <input value={tenantId} onChange={e => setTenantId(e.target.value)}
                 placeholder="00000000-0000-0000-0000-000000000000" style={inp} />
        </div>
      </div>

      <div style={row}>
        <label style={lbl}>Application (client) ID</label>
        <div style={{ flex: 1 }}>
          <input value={clientId} onChange={e => setClientId(e.target.value)}
                 placeholder="00000000-0000-0000-0000-000000000000" style={inp} />
          <div style={hint}>The <strong>ACE-Notifications</strong> registration — not the mail-capture one.</div>
        </div>
      </div>

      <div style={row}>
        <label style={lbl}>
          Client secret {secretSet && <span style={{ color: "#0F6E56", fontWeight: 500 }}>✓ set</span>}
        </label>
        <div style={{ flex: 1 }}>
          <input type="password" value={secret} onChange={e => setSecret(e.target.value)}
                 placeholder={secretSet ? "•••••••••  set — leave blank to keep" : "Not set"} style={inp} />
          <div style={hint}>
            Written to Key Vault as <code>{SECRET}</code> and never read back here. Entra secrets
            expire — when sending starts failing with “Entra rejected the app credentials”, this is
            the field to refill.
          </div>
        </div>
      </div>

      <div style={row}>
        <label style={lbl}>Send from</label>
        <div style={{ flex: 1 }}>
          <input value={sender} onChange={e => setSender(e.target.value)}
                 placeholder={"notices@" + org("emailDomain")} style={inp} />
          <div style={hint}>
            Must be the mailbox the Exchange role assignment is scoped to. Anything else and
            Graph answers 403 — that error names Exchange, not Entra.
          </div>
        </div>
      </div>

      <div style={row}>
        <label style={lbl}>Only send to</label>
        <div style={{ flex: 1 }}>
          <input value={domains} onChange={e => setDomains(e.target.value)}
                 placeholder={org("emailDomain")} style={inp} />
          <div style={hint}>
            Comma-separated domains. A backstop, not the main control: whatever the recipient
            query returns, nothing outside these domains is written to. Empty switches the
            check off.
          </div>
        </div>
      </div>

      <div style={{ ...row, alignItems: "center" }}>
        <label style={{ ...lbl, paddingTop: 0 }}>Active</label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
          <span>Send queued notifications</span>
        </label>
      </div>

      <div style={{ marginTop: 6 }}>
        <Btn variant="primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</Btn>
      </div>
    </Card>

    {/* ── The events ─────────────────────────────────────────────────────── */}
    <Card style={{ padding: 18, marginBottom: 16 }}>
      <SectionHeader title="Events" />

      {!evts ? (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
          Event settings are not available — run <code>sql/ace_notification_events.sql</code> and reload.
        </div>
      ) : (
        <>
          <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 6, lineHeight: 1.6 }}>
            What each notification says, and whether it fires at all. Switching an event off here
            stops it <em>queuing</em> — unlike Active above, which pauses delivery and keeps
            queuing. So an event switched off and back on does not release a backlog; there was
            never anything to release.
            <br />
            An empty box means the shipped wording is in use, shown greyed behind the cursor.
            Clearing a box you had edited restores that default rather than sending a blank line.
          </div>

          {!evts.installed && (
            <div style={{ margin: "10px 0 6px", padding: "9px 12px", borderRadius: 7, fontSize: 12.5,
                          background: "#fdeccd", color: "#7a4f00", lineHeight: 1.55 }}>
              <strong>Not migrated yet.</strong> Every event below is running on its shipped
              wording and is switched on — which is what it is actually doing. Run{" "}
              <code>sql/ace_notification_events.sql</code> to be able to change either.
            </div>
          )}

          {offCount > 0 && (
            <div style={{ margin: "10px 0 6px", padding: "9px 12px", borderRadius: 7, fontSize: 12.5,
                          background: "#f7d9cf", color: "#993C1D", lineHeight: 1.55 }}>
              <strong>{offCount} event{offCount === 1 ? " is" : "s are"} switched off.</strong>{" "}
              Nothing is being queued for {offCount === 1 ? "it" : "them"}, and nothing will arrive
              later to make up for the gap.
            </div>
          )}

          {(evts.events || []).map(ev => (
            <EventEditor key={ev.eventkey} ev={ev} busy={busy} onSave={saveEvent} />
          ))}

          {(evts.orphans || []).length > 0 && (
            <div style={{ marginTop: 14, padding: "10px 13px", borderRadius: 7, fontSize: 12.5,
                          background: "#fdeccd", color: "#7a4f00", lineHeight: 1.55 }}>
              <strong>Configured but not sendable.</strong> These rows exist in
              dbo.NotificationEvents with no handler in the code, so nothing can raise them.
              Either the handler was removed and the row was not, or the row is ahead of a deploy.
              <div style={{ marginTop: 5, fontFamily: "var(--font-mono)", fontSize: 11 }}>
                {evts.orphans.map(o => o.eventkey).join(", ")}
              </div>
            </div>
          )}
        </>
      )}
    </Card>

    {/* ── The queue ──────────────────────────────────────────────────────── */}
    <Card style={{ padding: 18 }}>
      <SectionHeader title="Queue" actions={failedCount > 0 ? [
        <Btn key="rq" onClick={() => act({ retryFailed: true }, r => `${r.requeued} message(s) requeued.`)}
             disabled={busy}>Retry {failedCount} failed</Btn>,
      ] : []} />

      {!stats ? (
        <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
          No outbox yet — run <code>sql/2026-09-14_notifications.sql</code> and reload.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 20, marginBottom: 14, flexWrap: "wrap" }}>
            {["pending", "sent", "failed"].map(s => (
              <div key={s}>
                <div style={{ fontSize: 22, fontWeight: 600 }}>{countOf(s)}</div>
                <div style={pill(s)}>{s}</div>
              </div>
            ))}
          </div>

          {failedCount > 0 && (
            <div style={{ marginBottom: 14, padding: "10px 13px", borderRadius: 7, fontSize: 12.5,
                          background: "#f7d9cf", color: "#993C1D", lineHeight: 1.55 }}>
              <strong>{failedCount} gave up.</strong> These are kept, not discarded — fix what the
              error names, then press Retry. A configuration failure fails every message the same
              way, so expect one cause rather than {failedCount}.
              {(stats.failed || []).slice(0, 2).map(f => (
                <div key={f.id} style={{ marginTop: 6, fontFamily: "var(--font-mono)", fontSize: 11 }}>
                  {f.recipient}: {f.lasterror}
                </div>
              ))}
            </div>
          )}

          {(stats.recent || []).length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>
              Nothing queued. Draws, ACH set-ups and risk changes queue themselves as they
              happen; monitoring findings are raised by a sweep &mdash; press <strong>Raise
              alerts now</strong> above, or wait for the Logic App.
            </div>
          ) : (
            <>
            {/* ── SAY WHICH SLICE THIS IS ──
                This table was capped at ten rows and said so nowhere, beside
                status counts that were uncapped. A monitoring sweep queues one
                row per finding per portfolio manager in a single second, so a
                message that had genuinely been sent simply was not on the list
                — and a truncated list that does not admit it reads as evidence
                the message was never queued. */}
            <div style={{ display: "flex", alignItems: "center", gap: 10,
                          flexWrap: "wrap", marginBottom: 8 }}>
              <input value={qFilter} onChange={e => setQFilter(e.target.value)}
                     placeholder="Filter by event, record, recipient or status…"
                     style={{ flex: "1 1 260px", minWidth: 180, padding: "6px 10px",
                              fontSize: 12.5, borderRadius: 6,
                              border: "1px solid var(--color-border-tertiary, #d7dbde)",
                              background: "transparent", color: "inherit" }} />
              <span style={{ fontSize: 12, color: "var(--color-text-secondary)",
                             whiteSpace: "nowrap" }}>
                {qFilter
                  ? `${shownRecent.length} of ${stats.recent.length} shown`
                  : stats.recentTotal > stats.recent.length
                    ? `Latest ${stats.recent.length} of ${stats.recentTotal}`
                    : `${stats.recent.length} message${stats.recent.length === 1 ? "" : "s"}`}
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
                <thead><tr>
                  <Th>When</Th><Th>Event</Th><Th>Record</Th><Th>To</Th><Th>Status</Th><Th>Tries</Th><Th>Last error</Th>
                </tr></thead>
                <tbody>
                  {shownRecent.map(r => (
                    <tr key={r.id} style={{ borderTop: "1px solid var(--color-border-tertiary, #e6e9eb)" }}>
                      <Td style={{ color: "var(--color-text-secondary)" }}>
                        {String(r.createdat || "").replace("T", " ").slice(0, 16)}</Td>
                      <Td>{r.eventkey}</Td>
                      <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{r.entityid}</span></Td>
                      <Td>{r.recipient}</Td>
                      <Td><span style={pill(r.status)}>{r.status}</span></Td>
                      <Td>{r.attempts}</Td>
                      <Td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis",
                                   color: "var(--color-text-secondary)" }}
                          title={r.lasterror || ""}>{r.lasterror || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {qFilter && shownRecent.length === 0 && (
              <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 8 }}>
                Nothing in the latest {stats.recent.length} matches that.
              </div>
            )}
            </>
          )}

          <div style={{ ...hint, marginTop: 12 }}>
            Delivery is not instant — the queue drains on the Logic App's schedule, five minutes
            being the suggested recurrence. <strong>Send queued now</strong> drains it immediately
            if you would rather not wait.
          </div>
        </>
      )}
    </Card>
  </>;
};
